
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Select,
  VStack,
  HStack,
  Text,
  Grid,
  Checkbox,
  Divider,
  Tooltip,
  IconButton,
  Badge,
  Flex,
} from '@chakra-ui/react'
import { Eye, Plus, Edit, Trash2, Download, Upload, ChevronRight, ChevronDown } from 'lucide-react'
import { createRoleSchema, updateRoleSchema, type CreateRoleFormData, type UpdateRoleFormData } from '@/lib/validations/auth'
import { apiClient } from '@/lib/api/client'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { useThemeColors } from '@/hooks/useThemeColors'

interface Navigation {
  id: number
  name: string
  parent_id: number | null
  children?: Navigation[]
  show_in_menu?: boolean | string // 'Show' | 'Hidden' from backend, or boolean
}

interface RoleFormProps {
  roleId?: number
  onSuccess?: () => void
  onCancel?: () => void
}

export function RoleForm({ roleId, onSuccess, onCancel }: RoleFormProps) {
  const colors = useThemeColors()
  const [navigations, setNavigations] = useState<Navigation[]>([])
  const [isLoadingNavigations, setIsLoadingNavigations] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isActive, setIsActive] = useState(true)
  const [expandedNavigations, setExpandedNavigations] = useState<Set<number>>(new Set())
  const isEditMode = !!roleId

  // Permission configuration with icons and labels
  const permissionConfig = [
    { name: 'can_view', label: 'View', icon: Eye, color: 'blue', description: 'View and read content' },
    { name: 'can_create', label: 'Create', icon: Plus, color: 'green', description: 'Create new items' },
    { name: 'can_edit', label: 'Edit', icon: Edit, color: 'orange', description: 'Modify existing items' },
    { name: 'can_delete', label: 'Delete', icon: Trash2, color: 'red', description: 'Remove items' },
    { name: 'can_export', label: 'Export', icon: Download, color: 'purple', description: 'Export data to files' },
    { name: 'can_import', label: 'Import', icon: Upload, color: 'teal', description: 'Import data from files' },
  ]

  const { showError, showSuccess, showWarning } = useToastNotification()

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<CreateRoleFormData | UpdateRoleFormData>({
    resolver: zodResolver(isEditMode ? updateRoleSchema : createRoleSchema),
    defaultValues: {
      name: '',
      active: true,
      permissions: {},
    },
  })

  const permissions = watch('permissions')
  const formActive = watch('active')

  // Exclude Email and Navigation from the List if the role isnt a developer
  const [currentUser, setCurrentUser] = useState<any>(null)

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await apiClient.get('/user/me')
        console.log('USER RESPONSE:', res.data)
        setCurrentUser(res.data?.data || res.data)
      } catch (error) {
        console.error('Failed to fetch user:', error)
      }
    }
    fetchUser()
  }, [])
  const isDeveloper = currentUser?.role_name === 'Developer Account'
  const EXCLUDED_NAMES = ['Email Settings', 'Navigation']

  const filterNavigations = (navs: Navigation[]): Navigation[] => {
    return navs.map((nav) => {
      if (nav.name === 'System Settings' && nav.children) {
        return {
          ...nav,
          children: nav.children.filter(
            (child) => !EXCLUDED_NAMES.includes(child.name)
          ),
        }
      }
      return nav
    })
  }
  
  // Sync isActive state with form value
  useEffect(() => {
    if (formActive !== undefined) {
      setIsActive(formActive)
    }
  }, [formActive])

  // Fetch navigations
  useEffect(() => {
    const fetchNavigations = async () => {
      try {
        const response = await apiClient.get('/options/routes')
        const navData = response.data?.data || response.data || []

        const normalizedNavs = Array.isArray(navData) ? navData : []

        // ✅ Only filter if NOT developer
        const finalNavs = isDeveloper
          ? normalizedNavs
          : filterNavigations(normalizedNavs)

        setNavigations(finalNavs)
      } catch (error: any) {
        showError(error)
      } finally {
        setIsLoadingNavigations(false)
      }
    }
    fetchNavigations()
  }, [showError])

  // Fetch role data for edit mode
  useEffect(() => {
    if (isEditMode && roleId) {
      const fetchRole = async () => {
        try {
          const response = await apiClient.get(`/user-management/roles/${roleId}`)
          const role = response.data?.data || response.data || {}

          // Normalize permissions - ensure all keys are strings (backend may return numbers)
          const normalizedPermissions: Record<string, Record<string, Record<string, boolean>>> = {}
          if (role.permissions && typeof role.permissions === 'object') {
            Object.keys(role.permissions).forEach((parentKey) => {
              const parentPermissions = role.permissions[parentKey]
              if (parentPermissions && typeof parentPermissions === 'object') {
                normalizedPermissions[String(parentKey)] = {}
                Object.keys(parentPermissions).forEach((navKey) => {
                  const navPermissions = parentPermissions[navKey]
                  if (navPermissions && typeof navPermissions === 'object') {
                    normalizedPermissions[String(parentKey)][String(navKey)] = {}
                    Object.keys(navPermissions).forEach((permName) => {
                      normalizedPermissions[String(parentKey)][String(navKey)][permName] = 
                        Boolean(navPermissions[permName])
                    })
                  }
                })
              }
            })
          }

          // Parse active status - backend returns 'Active' or 'Inactive' string
          const activeStatus = role.active === 'Active' || role.active === true || role.active === 1 || role.active === 'true'
          
          // Debug logging
          if (process.env.NODE_ENV === 'development') {
            console.log('[RoleForm] Loading role data:', {
              roleId,
              roleActive: role.active,
              activeType: typeof role.active,
              parsedActive: activeStatus,
              fullRole: role
            })
          }

          reset({
            name: role.name || '',
            active: activeStatus,
            permissions: normalizedPermissions,
          })

          setIsActive(activeStatus)
        } catch (error: any) {
          showError(error)
        }
      }
      fetchRole()
    }
  }, [isEditMode, roleId, reset, showError])

  const hasOtherPermissionSelected = (permissionSet: Record<string, boolean> = {}) => {
    return permissionConfig
      .filter((permission) => permission.name !== 'can_view')
      .some((permission) => permissionSet[permission.name] === true)
  }

  const ensureViewPermissionForSet = (permissionSet: Record<string, boolean> = {}) => {
    if (hasOtherPermissionSelected(permissionSet)) {
      permissionSet.can_view = true
    }
  }

  // Handle permission change
  const handlePermissionChange = (parentId: number, navigationId: number, permissionType: string, isChecked: boolean) => {
    const currentPermissions = permissions || {}
    const parentKey = parentId.toString()
    const navKey = navigationId.toString()

    const updatedPermissions = { ...currentPermissions }

    if (!updatedPermissions[parentKey]) {
      updatedPermissions[parentKey] = {}
    }

    if (!updatedPermissions[parentKey][navKey]) {
      updatedPermissions[parentKey][navKey] = {}
    }

    if (permissionType === 'can_view' && !isChecked && hasOtherPermissionSelected(updatedPermissions[parentKey][navKey])) {
      showWarning('View permission is required when other permissions are selected')
      return
    }

    updatedPermissions[parentKey][navKey][permissionType] = isChecked
    ensureViewPermissionForSet(updatedPermissions[parentKey][navKey])

    setValue('permissions', updatedPermissions, { shouldValidate: true })
    updateParentCheckboxState(parentId)
  }

  // Update parent checkbox state
  const updateParentCheckboxState = (parentId: number) => {
    const currentPermissions = permissions || {}
    const parentKey = parentId.toString()
    const parentNav = navigations.find((nav) => nav.id === parentId)

    if (!parentNav || !parentNav.children) return

    const permissionTypes = permissionConfig.map(p => p.name)

    permissionTypes.forEach((permType) => {
      const allChecked = parentNav.children!.every((child) => {
        const navKey = child.id.toString()
        return currentPermissions[parentKey]?.[navKey]?.[permType] === true
      })

      const someChecked = parentNav.children!.some((child) => {
        const navKey = child.id.toString()
        return currentPermissions[parentKey]?.[navKey]?.[permType] === true
      })

      const checkboxId = `parent-${parentId}-${permType}`
      const checkbox = document.getElementById(checkboxId) as HTMLInputElement
      if (checkbox) {
        checkbox.indeterminate = someChecked && !allChecked
        checkbox.checked = allChecked
      }
    })
  }

  // Handle check all change for parent - applies to all children recursively
  const handleCheckAllChange = (parentId: number, permissionType: string, isChecked: boolean) => {
    const currentPermissions = permissions || {}
    
    // Find the parent nav in the navigations tree
    const findNav = (navs: Navigation[], targetId: number): Navigation | null => {
      for (const nav of navs) {
        if (nav.id === targetId) return nav
        if (nav.children) {
          const found = findNav(nav.children, targetId)
          if (found) return found
        }
      }
      return null
    }
    
    const parentNav = findNav(navigations, parentId)
    if (!parentNav) return

    const updatedPermissions = { ...currentPermissions }

    // Determine the parent key for this navigation
    // For root items (parent_id = null), use 0 as parent key (matching backend)
    const parentNavParentId = parentNav.parent_id ?? 0
    const parentNavParentKey = parentNavParentId.toString()
    const parentNavKey = parentId.toString()

    const hasAnyNonViewPermissionInTree = (nav: Navigation): boolean => {
      const navParentKey = (nav.parent_id ?? 0).toString()
      const navTreeKey = nav.id.toString()
      const navPermissions = updatedPermissions[navParentKey]?.[navTreeKey] || {}
      const currentNavHasOtherPermission = hasOtherPermissionSelected(navPermissions)

      if (currentNavHasOtherPermission) {
        return true
      }

      if (!nav.children || nav.children.length === 0) {
        return false
      }

      return nav.children.some((child) => hasAnyNonViewPermissionInTree(child))
    }

    if (permissionType === 'can_view' && !isChecked && hasAnyNonViewPermissionInTree(parentNav)) {
      showWarning('You can only deselect View permission when no other permissions are selected')
      return
    }

    // Also save permission for the parent navigation itself
    // This is needed for case 2: parent route with hidden children (like bookings)
    // The backend checks parent permissions to generate create/edit routes
    if (!updatedPermissions[parentNavParentKey]) {
      updatedPermissions[parentNavParentKey] = {}
    }
    if (!updatedPermissions[parentNavParentKey][parentNavKey]) {
      updatedPermissions[parentNavParentKey][parentNavKey] = {}
    }
    updatedPermissions[parentNavParentKey][parentNavKey][permissionType] = isChecked
    ensureViewPermissionForSet(updatedPermissions[parentNavParentKey][parentNavKey])

    // If parent has children, also apply to all children recursively
    if (parentNav.children && parentNav.children.length > 0) {
      // Apply permission to all children recursively
      const applyPermissionToChildren = (nav: Navigation) => {
        const navKey = nav.id.toString()
        // Children use their parent's ID as the parent key
        const childParentKey = parentNavKey
        if (!updatedPermissions[childParentKey]) {
          updatedPermissions[childParentKey] = {}
        }
        if (!updatedPermissions[childParentKey][navKey]) {
          updatedPermissions[childParentKey][navKey] = {}
        }
        updatedPermissions[childParentKey][navKey][permissionType] = isChecked
        ensureViewPermissionForSet(updatedPermissions[childParentKey][navKey])

        if (nav.children && nav.children.length > 0) {
          nav.children.forEach((child) => applyPermissionToChildren(child))
        }
      }

      // Apply to all direct children and their descendants
      parentNav.children.forEach((child) => applyPermissionToChildren(child))
    }

    setValue('permissions', updatedPermissions, { shouldValidate: true })
  }

  // Toggle navigation expansion
  const toggleNavigation = (navId: number) => {
    setExpandedNavigations((prev) => {
      const next = new Set(prev)
      if (next.has(navId)) {
        next.delete(navId)
      } else {
        next.add(navId)
      }
      return next
    })
  }


  // Render permissions recursively
  const renderPermissions = () => {
    const renderNavigationRow = (item: Navigation, parentId: number | null = null, level: number = 0) => {
      const paddingLeft = `${level * 24}px`
      const hasChildren = item.children && item.children.length > 0
      const isTopLevel = level === 0
      const isExpanded = expandedNavigations.has(item.id)
      // For root items, use 0 as parent key (matching backend)
      const effectiveParentId = parentId !== null ? parentId : (item.parent_id ?? 0)
      const parentKey = effectiveParentId.toString()
      const navKey = item.id.toString()

      // Calculate if all children are checked (for parent items with children)
      const calculateAllChecked = (permissionType: string) => {
        if (!hasChildren) {
          // For single nav without children, check if this nav has the permission
          return permissions?.[parentKey]?.[navKey]?.[permissionType] === true
        }
        
        // For parent navs with children, check:
        // 1. If parent itself has the permission (needed for case 2: parent with hidden children)
        // 2. If all children have the permission
        const parentHasPermission = permissions?.[parentKey]?.[navKey]?.[permissionType] === true
        
        // Need to check all descendants recursively using their correct parent keys
        const checkAllDescendants = (nav: Navigation, navParentId: number): boolean => {
          // Use the nav's parent_id (or 0 if root) as the parent key
          const navParentKey = navParentId.toString()
          const childNavKey = nav.id.toString()
          const hasPermission = permissions?.[navParentKey]?.[childNavKey]?.[permissionType] === true
          
          // If this nav has children, all of them must also have the permission
          if (nav.children && nav.children.length > 0) {
            return hasPermission && nav.children.every(child => checkAllDescendants(child, nav.id))
          }
          
          return hasPermission
        }
        
        // Check all direct children using the parent nav's id as their parent key
        const allChildrenHavePermission = item.children!.every((child) => checkAllDescendants(child, item.id))
        
        // For case 2 (parent with hidden children), parent permission is required
        // For case 3 (parent with visible children), only children permissions matter
        // We check both to be safe - if parent has permission AND all children have permission, it's checked
        return parentHasPermission && allChildrenHavePermission
      }

      // Calculate if any permission is checked for this navigation
      const hasAnyPermission = permissionConfig.some((perm) => {
        return permissions?.[parentKey]?.[navKey]?.[perm.name] === true
      })

      // Calculate if all permissions are checked for this navigation
      const allPermissionsChecked = permissionConfig.every((perm) => {
        return permissions?.[parentKey]?.[navKey]?.[perm.name] === true
      })

      const bgColor = isTopLevel
        ? colors.sectionBg
        : hasAnyPermission
          ? colors.uploadHover
          : colors.cardBg

      return (
        <Box key={item.id}>
          <Box
            bg={bgColor}
            borderBottom="1px solid"
            borderColor={colors.border}
            _hover={{
              bg: colors.sectionBg,
            }}
            transition="background-color 0.2s"
          >
            <Grid
              templateColumns={{ base: '1fr', md: '2fr repeat(6, 1fr)' }}
              gap={2}
              py={3}
              px={4}
              alignItems="center"
            >
              {/* Navigation Name with Expand/Collapse */}
              <Flex alignItems="center" gap={2} pl={paddingLeft}>
                {hasChildren && (
                  <IconButton
                    aria-label={isExpanded ? 'Collapse' : 'Expand'}
                    icon={isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    size="xs"
                    variant="ghost"
                    onClick={() => toggleNavigation(item.id)}
                  />
                )}
                {!hasChildren && <Box w="24px" />}
                <Text
                  fontWeight={isTopLevel ? 'bold' : 'medium'}
                  fontSize="sm"
                  color={colors.heading}
                >
                  {item.name}
                </Text>
                {hasAnyPermission && (
                  <Badge colorScheme="blue" size="sm" ml={2}>
                    {permissionConfig.filter((perm) => 
                      permissions?.[parentKey]?.[navKey]?.[perm.name] === true
                    ).length}/{permissionConfig.length}
                  </Badge>
                )}
              </Flex>

              {/* Permission Checkboxes */}
              {permissionConfig.map((perm) => {
                const Icon = perm.icon
                // Determine if checkbox should be checked
                let isChecked: boolean
                if (hasChildren) {
                  // Parent nav with children - check if all children (and their descendants) are checked
                  isChecked = calculateAllChecked(perm.name)
                } else {
                  // Single nav or child nav - check if this nav has the permission
                  isChecked = permissions?.[parentKey]?.[navKey]?.[perm.name] === true
                }

                return (
                  <Tooltip key={perm.name} label={perm.description} hasArrow>
                    <Box 
                      display="flex" 
                      justifyContent="center" 
                      alignItems="center" 
                      whiteSpace="nowrap"
                      h="100%"
                    >
                      <Checkbox
                        id={hasChildren ? `parent-${item.id}-${perm.name}` : `nav-${item.id}-${perm.name}`}
                        isChecked={isChecked}
                        colorScheme={perm.color}
                        onChange={(e) => {
                          const checked = e.target.checked
                          if (hasChildren) {
                            // Parent nav with children - apply to all children recursively
                            handleCheckAllChange(item.id, perm.name, checked)
                          } else {
                            // Single nav or child nav - apply to this nav only
                            handlePermissionChange(effectiveParentId, item.id, perm.name, checked)
                          }
                        }}
                      >
                        <Box display={{ base: 'none', md: 'flex' }} alignItems="center" gap={1} ml={1} whiteSpace="nowrap">
                          <Icon size={14} />
                          <Text fontSize="xs">{perm.label}</Text>
                        </Box>
                      </Checkbox>
                    </Box>
                  </Tooltip>
                )
              })}
            </Grid>
          </Box>

          {/* Render Children Recursively */}
          {hasChildren && isExpanded &&
            item.children!.map((child) => renderNavigationRow(child, item.id, level + 1))}
        </Box>
      )
    }

    if (isLoadingNavigations) {
      return <Text>Loading permissions...</Text>
    }

    return (
      <Box>
        {/* Header Row */}
        <Box
          bg={colors.cardBg}
          borderBottom="2px solid"
          borderColor={colors.border}
          mb={2}
          shadow="sm"
        >
          <Grid
            templateColumns={{ base: '1fr', md: '2fr repeat(6, 1fr)' }}
            gap={2}
            py={3}
            px={4}
            alignItems="center"
          >
            <Flex alignItems="center" gap={2}>
              <Box w="24px" />
              <Text fontWeight="bold" fontSize="sm" color={colors.heading}>
                Navigation
              </Text>
            </Flex>
            {permissionConfig.map((perm) => {
              const Icon = perm.icon
              return (
                <Box 
                  key={perm.name} 
                  display="flex" 
                  alignItems="center" 
                  justifyContent="center" 
                  whiteSpace="nowrap"
                  h="100%"
                >
                  <Box display={{ base: 'none', md: 'flex' }} alignItems="center" gap={1}>
                    <Icon size={14} />
                    <Text fontWeight="bold" fontSize="xs" color={colors.heading}>
                      {perm.label}
                    </Text>
                  </Box>
                </Box>
              )
            })}
          </Grid>
        </Box>

        {/* Navigation Rows */}
        <Box>
          {navigations.length === 0 && !isLoadingNavigations && (
            <Box textAlign="center" py={8}>
              <Text color={colors.textSecondary}>
                No navigations available. Please configure navigations first.
              </Text>
            </Box>
          )}
          {navigations.map((nav) => {
            // Root items have parent_id = null, use 0 as parent key (matching backend)
            const parentId = nav.parent_id ?? 0
            return (
              <Box
                key={nav.id}
                borderTop="2px solid"
                borderColor={colors.border}
                _first={{ borderTop: 'none' }}
              >
                {renderNavigationRow(nav, parentId, 0)}
              </Box>
            )
          })}
        </Box>
      </Box>
    )
  }

  const onSubmit = async (data: CreateRoleFormData | UpdateRoleFormData) => {
    setIsSubmitting(true)
    try {
      const payload: any = {
        name: data.name,
        active: isActive,
        permissions: data.permissions,
      }

      // Debug logging in development
      if (process.env.NODE_ENV === 'development') {
        console.log('[RoleForm] Submitting payload:', {
          mode: isEditMode ? 'UPDATE' : 'CREATE',
          roleId: isEditMode ? roleId : 'new',
          payload,
          permissionsStructure: {
            keys: Object.keys(payload.permissions || {}),
            sample: Object.keys(payload.permissions || {}).slice(0, 2).reduce((acc, key) => {
              acc[key] = payload.permissions[key]
              return acc
            }, {} as any)
          }
        })
      }

      if (isEditMode) {
        const response = await apiClient.put(`/user-management/roles/${roleId}`, payload)
        if (process.env.NODE_ENV === 'development') {
          console.log('[RoleForm] Update response:', response.data)
        }
        showSuccess('Success', 'Role updated successfully')
      } else {
        const response = await apiClient.post('/user-management/roles', payload)
        if (process.env.NODE_ENV === 'development') {
          console.log('[RoleForm] Create response:', response.data)
        }
        showSuccess('Success', 'Role created successfully')
      }

      onSuccess?.()
    } catch (error: any) {
      if (process.env.NODE_ENV === 'development') {
        console.error('[RoleForm] Submission error:', {
          error,
          response: error.response?.data,
          status: error.response?.status,
          message: error.message,
        })
      }
      showError(error)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Box as="form" onSubmit={handleSubmit(onSubmit)}>
      <VStack spacing={4} align="stretch">
        {/* Name Field */}
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Name <Text as="span" color="red.500">*</Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.name}>
            <Input
              {...register('name')}
              placeholder="e.g. Administrator, Editor, Viewer"
            />
            <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        {/* Active Field */}
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Active</FormLabel>
          <FormControl>
            <Checkbox
              isChecked={isActive}
              onChange={(e) => {
                setIsActive(e.target.checked)
                setValue('active', e.target.checked)
              }}
            >
              Active
            </Checkbox>
          </FormControl>
        </Grid>

        <Divider />

        {/* Permissions Field */}
        <VStack align="stretch" spacing={3}>
          <HStack justify="space-between" flexWrap="wrap" gap={2}>
            <FormLabel fontWeight="medium" mb={0}>
              Permissions <Text as="span" color="red.500">*</Text>
            </FormLabel>
            <HStack spacing={2}>
              <Button
                size="sm"
                variant="outline"
                colorScheme="blue"
                onClick={() => {
                  // Expand all
                  const allIds = new Set<number>()
                  const collectIds = (navs: Navigation[]) => {
                    navs.forEach((nav) => {
                      if (nav.children && nav.children.length > 0) {
                        allIds.add(nav.id)
                        collectIds(nav.children)
                      }
                    })
                  }
                  collectIds(navigations)
                  setExpandedNavigations(allIds)
                }}
              >
                Expand All
              </Button>
              <Button
                size="sm"
                variant="outline"
                colorScheme="blue"
                onClick={() => setExpandedNavigations(new Set())}
              >
                Collapse All
              </Button>
            </HStack>
          </HStack>
          <Text fontSize="xs" color={colors.text}>
            Select permissions for each navigation. Use parent checkboxes to select all children at once.
          </Text>
          <FormControl isInvalid={!!errors.permissions}>
            <Box
              border="1px solid"
              borderColor={colors.border}
              borderRadius="md"
              bg={colors.cardBg}
              shadow="sm"
            >
              {renderPermissions()}
            </Box>
            <FormErrorMessage>
              {typeof errors.permissions === 'object' && 'message' in errors.permissions
                ? String(errors.permissions.message)
                : 'Please select at least one permission'}
            </FormErrorMessage>
          </FormControl>
        </VStack>

        {/* Action Buttons */}
        <HStack spacing={4} justify="flex-end" pt={4}>
          {onCancel && (
            <Button
              variant="outline"
              onClick={onCancel}
              isDisabled={isSubmitting}
            >
              Cancel
            </Button>
          )}
          <Button
            type="submit"
            colorScheme="blue"
            isLoading={isSubmitting}
            loadingText={isEditMode ? 'Saving...' : 'Creating...'}
          >
            {isEditMode ? 'Save' : 'Create Role'}
          </Button>
        </HStack>
      </VStack>
    </Box>
  )
}

